<script lang="ts" setup>
import type { CartItem } from '~/types/Cart';
import type { OfferFull, OfferMedium } from '~/types/Catalog';
import { getFavoriteOffer } from '~/utils/multicard';

const props = defineProps<{ offer: OfferMedium | OfferFull | CartItem['data']; selectedOfferId?: number | null }>();
const catalogStore = useCatalogStore();
const { sendOfferEventToDataLayer } = useGtmModule();

const favoriteOffer = computed(() => getFavoriteOffer(props.offer, props.selectedOfferId ?? null));
const favoriteOfferId = computed(() => favoriteOffer.value?.id ?? null);
const offerForAnalytics = computed(() =>
  favoriteOffer.value ? ({ ...props.offer, ...favoriteOffer.value } as OfferMedium | OfferFull) : props.offer,
);
const isAddedToFavorites = computed(() => {
  return favoriteOfferId.value !== null && catalogStore.favoriteOffersIds.includes(favoriteOfferId.value);
});
const isLoading = ref(false);

const computedClasses = computed(() => [{ 'favorite-btn__icon--active': isAddedToFavorites.value }]);

async function switchFavoriteState() {
  if (isLoading.value || favoriteOfferId.value === null) return;

  const offerId = favoriteOfferId.value;
  const wasAddedToFavorites = isAddedToFavorites.value;
  isLoading.value = true;

  try {
    const isUpdated = await catalogStore.toggleFavorite(offerId);

    if (!isUpdated) return;

    const eventName = wasAddedToFavorites ? 'remove_from_wishlist' : 'add_to_wishlist';
    sendOfferEventToDataLayer(eventName, offerForAnalytics.value);
    window.roistat?.event?.send(eventName);
    if (window.r46) window.r46('track', wasAddedToFavorites ? 'remove_wish' : 'wish', offerId);
  } finally {
    isLoading.value = false;
  }
}
</script>

<template>
  <button
    :disabled="isLoading || favoriteOfferId === null"
    name="favorite-btn"
    @click.stop.prevent="switchFavoriteState"
  >
    <SvgoHeart
      :class="computedClasses"
      class="favorite-btn__icon"
      filled
    />
  </button>
</template>

<style lang="scss" scoped>
.favorite-btn {
  &__icon {
    width: 24px;
    height: 24px;
    color: $black-40;
    fill: $white;
    transition: all 0.2s;

    &:hover {
      @include screen-md {
        fill: $black-10;
      }
    }

    &--active {
      fill: $label-red;

      &:hover {
        @include screen-md {
          fill: $label-red-dark;
        }
      }
    }
  }
}
</style>
